/**
 * Research query hooks for company profile data
 *
 * Provides React Query hooks for:
 * - Phase 7A: Company fundamentals/metrics
 * - Phase 7B: SEC filings
 * - Phase 7C: Insider trades and institutional holdings
 */
import { useQuery } from '@tanstack/react-query'
import {
  fetchResearchableSymbols,
  fetchCompanyMetrics,
  fetchSecFilings,
  fetchFilingDetail,
  fetchInsiderTrades,
  fetchInsiderSummary,
  fetchInstitutionalHoldings,
  fetchInstitutionalSummary,
  type FetchSecFilingsParams,
  type FetchInsiderTradesParams,
  type FetchInstitutionalHoldingsParams,
} from '../api/research'
import type {
  CompanyMetrics,
  FilingsResponse,
  FilingDetail,
  InsiderTradesResponse,
  InsiderSummary,
  InstitutionalHoldingsResponse,
  InstitutionalSummary,
} from '../types/research'

// =============================================================================
// Query Keys
// =============================================================================

export const RESEARCH_KEYS = {
  researchableSymbols: () => ['researchable-symbols'] as const,
  metrics: (symbol: string) => ['company-metrics', symbol] as const,
  filings: (symbol: string, params?: FetchSecFilingsParams) =>
    ['sec-filings', symbol, params] as const,
  filingDetail: (filingId: string) => ['sec-filing-detail', filingId] as const,
  insiderTrades: (symbol: string, params?: FetchInsiderTradesParams) =>
    ['insider-trades', symbol, params] as const,
  insiderSummary: (symbol: string, days?: number) =>
    ['insider-summary', symbol, days] as const,
  institutionalHoldings: (symbol: string, params?: FetchInstitutionalHoldingsParams) =>
    ['institutional-holdings', symbol, params] as const,
  institutionalSummary: (symbol: string, period?: string) =>
    ['institutional-summary', symbol, period] as const,
}

// =============================================================================
// Fundamentals Hooks (Phase 7A)
// =============================================================================

/**
 * Fetch all symbols that can be researched (any data available).
 * Includes fundamentals, price history, and alert-tracked symbols.
 * Used to populate the symbol dropdown with all researchable symbols.
 */
export function useResearchableSymbols() {
  return useQuery<string[]>({
    queryKey: RESEARCH_KEYS.researchableSymbols(),
    queryFn: fetchResearchableSymbols,
    staleTime: 5 * 60 * 1000, // 5 minutes - symbols list changes infrequently
  })
}

/**
 * Fetch company financial metrics (valuation, profitability, growth)
 * @param symbol Stock ticker symbol (undefined disables query)
 */
export function useCompanyMetrics(symbol: string | undefined) {
  return useQuery<CompanyMetrics>({
    queryKey: RESEARCH_KEYS.metrics(symbol ?? ''),
    queryFn: () => fetchCompanyMetrics(symbol!),
    enabled: !!symbol,
  })
}

// =============================================================================
// SEC Filings Hooks (Phase 7B)
// =============================================================================

/**
 * Fetch SEC filings for a symbol
 * @param symbol Stock ticker symbol (undefined disables query)
 * @param params Optional filters (filingTypes, limit, offset)
 */
export function useSecFilings(
  symbol: string | undefined,
  params?: FetchSecFilingsParams
) {
  return useQuery<FilingsResponse>({
    queryKey: RESEARCH_KEYS.filings(symbol ?? '', params),
    queryFn: () => fetchSecFilings(symbol!, params),
    enabled: !!symbol,
  })
}

/**
 * Fetch detailed filing with sections and summary
 * @param filingId Filing ID (undefined disables query)
 */
export function useFilingDetail(filingId: string | undefined) {
  return useQuery<FilingDetail>({
    queryKey: RESEARCH_KEYS.filingDetail(filingId ?? ''),
    queryFn: () => fetchFilingDetail(Number(filingId)),
    enabled: !!filingId,
  })
}

// =============================================================================
// Insider Trades Hooks (Phase 7C)
// =============================================================================

/**
 * Fetch insider trades for a symbol
 * @param symbol Stock ticker symbol (undefined disables query)
 * @param params Optional filters (days, transactionTypes, limit, offset)
 */
export function useInsiderTrades(
  symbol: string | undefined,
  params?: FetchInsiderTradesParams
) {
  return useQuery<InsiderTradesResponse>({
    queryKey: RESEARCH_KEYS.insiderTrades(symbol ?? '', params),
    queryFn: () => fetchInsiderTrades(symbol!, params),
    enabled: !!symbol,
  })
}

/**
 * Fetch insider activity summary for a symbol
 * @param symbol Stock ticker symbol (undefined disables query)
 * @param days Lookback period in days (default: 90)
 */
export function useInsiderSummary(symbol: string | undefined, days?: number) {
  return useQuery<InsiderSummary>({
    queryKey: RESEARCH_KEYS.insiderSummary(symbol ?? '', days),
    queryFn: () => fetchInsiderSummary(symbol!, days),
    enabled: !!symbol,
  })
}

// =============================================================================
// Institutional Holdings Hooks (Phase 7C)
// =============================================================================

/**
 * Fetch institutional holdings for a symbol
 * @param symbol Stock ticker symbol (undefined disables query)
 * @param params Optional filters (period, limit, offset)
 */
export function useInstitutionalHoldings(
  symbol: string | undefined,
  params?: FetchInstitutionalHoldingsParams
) {
  return useQuery<InstitutionalHoldingsResponse>({
    queryKey: RESEARCH_KEYS.institutionalHoldings(symbol ?? '', params),
    queryFn: () => fetchInstitutionalHoldings(symbol!, params),
    enabled: !!symbol,
  })
}

/**
 * Fetch institutional ownership summary for a symbol
 * @param symbol Stock ticker symbol (undefined disables query)
 * @param period Optional quarter end date (YYYY-MM-DD)
 */
export function useInstitutionalSummary(
  symbol: string | undefined,
  period?: string
) {
  return useQuery<InstitutionalSummary>({
    queryKey: RESEARCH_KEYS.institutionalSummary(symbol ?? '', period),
    queryFn: () => fetchInstitutionalSummary(symbol!, period),
    enabled: !!symbol,
  })
}
